import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { ModalController } from "@ionic/angular/standalone";

// Id de la modale : le dismiss cible explicitement cette modale, pas la plus haute présentée.
export const OTA_RESTART_SHEET_ID = "ota-restart-sheet";

/**
 * Modale bloquante de mise à jour OTA obligatoire.
 *
 * Affiché par OtaService une fois le bundle OTA téléchargé.
 * Rendu obligatoire : aucun bouton d'annulation ou "plus tard",
 * aucun dismiss par swipe/backdrop, seul le bouton "Mettre à jour"
 * permet d'appliquer et relancer l'application via le rôle `restart`.
 */
@Component({
	selector: "app-ota-restart-sheet",
	imports: [TranslatePipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<div class="ota-sheet tw-pt-6">
			<div class="ota-sheet__glyph-wrap">
				<div class="ota-sheet__glyph">
					B3
					<span class="ota-sheet__badge" aria-hidden="true">
						<i class="material-icons-outlined ota-sheet__badge-icon">autorenew</i>
					</span>
				</div>
			</div>

			<h2 class="ota-sheet__title">{{ "ota.update_ready_title" | translate }}</h2>
			<p class="ota-sheet__message">{{ "ota.restart_to_apply" | translate }}</p>

			<div class="ota-sheet__info">
				<i class="material-icons-outlined ota-sheet__info-icon" aria-hidden="true">info</i>
				<span class="ota-sheet__info-text">{{ "ota.restart_info" | translate }}</span>
			</div>

			<div class="ota-sheet__actions">
				<button
					type="button"
					class="ota-sheet__btn ota-sheet__btn--primary"
					[disabled]="isUpdating()"
					(click)="restart()"
				>
					<i
						class="material-icons-outlined ota-sheet__btn-icon"
						[class.tw-animate-spin]="isUpdating()"
						aria-hidden="true"
						>autorenew</i
					>
					@if (isUpdating()) {
						{{ "ota.updating" | translate }}
					} @else {
						{{ "ota.restart_now" | translate }}
					}
				</button>
			</div>
		</div>
	`,
	styles: [
		`
			:host {
				display: block;
				font-family: Inter, sans-serif;
			}
			.ota-sheet {
				background: #ffffff;
				padding: 24px 20px calc(36px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)));
			}
			.ota-sheet__glyph-wrap {
				display: flex;
				justify-content: center;
				margin-bottom: 14px;
			}
			.ota-sheet__glyph {
				position: relative;
				width: 64px;
				height: 64px;
				border-radius: 16px;
				background: linear-gradient(135deg, #f9b200, #ffd45e);
				display: flex;
				align-items: center;
				justify-content: center;
				font-weight: 800;
				font-size: 27px;
				color: #111827;
				letter-spacing: -0.04em;
				box-shadow:
					0 6px 16px -4px rgba(249, 178, 0, 0.45),
					inset 0 1px 0 rgba(255, 255, 255, 0.5);
			}
			.ota-sheet__badge {
				position: absolute;
				right: -4px;
				bottom: -4px;
				width: 22px;
				height: 22px;
				border-radius: 999px;
				background: #005e88;
				border: 2px solid #fff;
				display: flex;
				align-items: center;
				justify-content: center;
			}
			.ota-sheet__badge-icon {
				font-size: 12px;
				color: #fff;
			}
			.ota-sheet__title {
				text-align: center;
				font-size: 18px;
				font-weight: 700;
				color: #111827;
				letter-spacing: -0.01em;
				margin: 0 0 6px;
			}
			.ota-sheet__message {
				text-align: center;
				font-size: 13px;
				color: #6b7280;
				line-height: 1.5;
				margin: 0 0 18px;
			}
			.ota-sheet__info {
				display: flex;
				gap: 8px;
				align-items: center;
				padding: 10px 12px;
				background: #f9fafb;
				border: 1px solid #f3f4f6;
				border-radius: 10px;
				margin-bottom: 20px;
				font-size: 12px;
				color: #4b5563;
				line-height: 1.4;
			}
			.ota-sheet__info-icon {
				font-size: 16px;
				color: #005e88;
				flex-shrink: 0;
			}
			.ota-sheet__info-text {
				flex: 1;
			}
			.ota-sheet__actions {
				display: flex;
				flex-direction: column;
			}
			.ota-sheet__btn {
				border: none;
				border-radius: 10px;
				cursor: pointer;
				font-family: Inter, sans-serif;
				font-size: 14px;
				font-weight: 600;
				display: inline-flex;
				align-items: center;
				justify-content: center;
				gap: 6px;
				transition:
					background 150ms,
					opacity 150ms;
			}
			.ota-sheet__btn:disabled {
				opacity: 0.7;
				cursor: not-allowed;
			}
			.ota-sheet__btn-icon {
				font-size: 18px;
			}
			.ota-sheet__btn--primary {
				height: 48px;
				background: #f9b200;
				color: #111827;
			}
			.ota-sheet__btn--primary:active:not(:disabled) {
				background: #e6a000;
			}
		`
	]
})
export class OtaRestartSheetComponent {
	private readonly modalCtrl = inject(ModalController);
	readonly isUpdating = signal(false);

	restart(): void {
		if (this.isUpdating()) {
			return;
		}
		this.isUpdating.set(true);
		this.modalCtrl.dismiss(null, "restart", OTA_RESTART_SHEET_ID);
	}
}
